import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Delete, Eye, Pencil, Trash, Settings } from "lucide-react";
import { Switch, Menu, MenuItem, IconButton, Checkbox } from "@mui/material";
import { deepPurple, purple } from "@mui/material/colors";
import { NavLink } from "react-router-dom";
import { Permissions } from "../../../../permissions";
import { errorToast, successToast } from "../../../../services/toast.service";
import {
  useFetchCustomersQuery,
  useUpdateCustomerStatusMutation,
} from "../../../../services/baseQuery";
import { CircularProgress } from "@mui/material";
import { useCallback, useEffect } from "react";

const defaultColumns = [
  { id: "customerId", label: "Customer ID", minWidth: "140px", required: true },
  { id: "firmName", label: "Firm Name", minWidth: "200px", required: true },
  { id: "ownerName", label: "Owner Name", minWidth: "180px", required: false },
  {
    id: "regPhoneNumber",
    label: "Phone Number",
    minWidth: "140px",
    required: true,
  },
  { id: "storeType", label: "Store Type", minWidth: "120px", required: false },
  {
    id: "shopAddress",
    label: "Shop Address",
    minWidth: "200px",
    required: false,
  },
  { id: "gstin", label: "GSTIN", minWidth: "180px", required: false },
  {
    id: "creditLimit",
    label: "Credit Limit",
    minWidth: "120px",
    required: false,
  },
  { id: "status", label: "Status", minWidth: "100px", required: true },
  { id: "action", label: "Action", minWidth: "120px", required: true },
];

export default function ViewCustomerTable({
  onSettingsClick,
  anchorEl,
  onCloseSettings,
  visibleColumns,
  onColumnToggle,
  setRefetchFn,
  searchQuery,
  filters,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const {
    data: customersData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useFetchCustomersQuery({
    page: page + 1,
    limit: rowsPerPage,
    search: searchQuery,
    filters: filters,
  });

  // Reset to first page when filters or search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filters]);

  const [updateStatus] = useUpdateCustomerStatusMutation();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Filter visible columns
  const visibleColumnsList = defaultColumns.filter(
    column => visibleColumns[column.id],
  );

  let editcustomer =
    Permissions.find(permission => permission.includes("edit_customer")) !==
    undefined;

  let deletecustomer =
    Permissions.find(permission => permission.includes("delete_customer")) !==
    undefined;

  // Handle status change
  const handleStatusChange = async (customerId, currentStatus) => {
    try {
      await updateStatus({
        customerId,
        status: currentStatus === "Active" ? true : false,
      }).unwrap();

      successToast(
        `Customer ${
          currentStatus === "Active" ? "deactivated" : "activated"
        } successfully`,
      );
    } catch (error) {
      errorToast(
        error.data?.error?.message || "Failed to update customer status",
      );
    }
  };

  // Transform API data to table format
  const rows = React.useMemo(() => {
    if (!customersData?.data?.customers) return [];

    return customersData.data.customers.map(customer => ({
      _id: customer._id,
      customerId: customer.storeId,
      firmName: customer.businessName,
      ownerName: customer.ownerName,
      regPhoneNumber: customer.registeredPhoneNo,
      storeType:
        customer.storeType === "WHOLESALER" ? "Wholesaler" : "Retailer",
      shopAddress: `${customer.shopAddress.locality}, ${customer.shopAddress.city_district}`,
      gstin: customer.GSTIN,
      creditLimit: `₹${customer.vcConfigSettings.creditLimit.toLocaleString()}`,
      status: customer.customerBlocked ? "Blocked" : "Active",
    }));
  }, [customersData]);

  // Pass refetch function to parent
  React.useEffect(() => {
    if (typeof setRefetchFn === "function") {
      setRefetchFn(() => refetch);
    }
  }, [setRefetchFn, refetch]);

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: "none",
        border: "1px solid #E5E7EB",
        borderRadius: "0.5rem",
      }}>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseSettings}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 200,
          },
        }}>
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-600">Show Columns</p>
        </div>
        {defaultColumns.map(column => (
          <MenuItem
            key={column.id}
            onClick={() => onColumnToggle(column.id)}
            disabled={column.required}
            dense>
            <Checkbox
              checked={visibleColumns[column.id]}
              disabled={column.required}
              size="small"
              sx={{
                color: "#5652B0",
                "&.Mui-checked": {
                  color: "#5652B0",
                },
              }}
            />
            <span
              className={`text-sm ${column.required ? "text-gray-400" : ""}`}>
              {column.label}
              {column.required && " *"}
            </span>
          </MenuItem>
        ))}
      </Menu>

      <TableContainer
        sx={{ maxHeight: "calc(100vh - 300px)" }}
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumnsList.map(column => (
                <TableCell
                  key={column.id}
                  align="center"
                  sx={{
                    minWidth: column.minWidth,
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={40} />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <div className="text-red-500 py-4">
                    {error.data?.error?.message || "Error loading customers"}
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <div className="py-4 text-gray-500">No customers found</div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row._id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                  }}>
                  {visibleColumnsList.map(column => {
                    const value = row[column.id];
                    return (
                      <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {column.id === "status" ? (
                          <div
                            className={`px-3 py-1 rounded-full text-sm font-medium inline-block
                            ${
                              value === "Active"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}>
                            {value}
                          </div>
                        ) : column.id === "action" ? (
                          <div className="flex items-center justify-center gap-3">
                            <NavLink
                              to={`/customer/view/${row._id}`}
                              className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                              <Eye size={16} className="text-[#5652B0]" />
                            </NavLink>

                            {editcustomer ? (
                              <NavLink
                                to="/customer/edit"
                                className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                                <Pencil size={16} className="text-[#5652B0]" />
                              </NavLink>
                            ) : (
                              <div className="p-1.5 rounded-md bg-gray-100 cursor-not-allowed">
                                <Pencil size={16} className="text-gray-400" />
                              </div>
                            )}
                          </div>
                        ) : (
                          value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={customersData?.data?.totalCount || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: "1px solid #E5E7EB",
          ".MuiTablePagination-select": {
            borderRadius: "0.375rem",
          },
        }}
      />

      {isFetching && isLoading && (
        <div className="absolute top-0 left-0 right-0">
          <div className="h-1 w-full overflow-hidden">
            <div className="animate-progress bg-[#5652B0] h-1"></div>
          </div>
        </div>
      )}
    </Paper>
  );
}

export { defaultColumns };
